import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';
import { Copy, Check, RefreshCw, Trash2, Link2 } from 'lucide-react';
import { cn } from '@siliconx/shared/ui/utils';
import { LoadingShell } from '@siliconx/shared/ui/state';
import { Button } from '@siliconx/shared/ui/Button';
import { PageHeader } from '@siliconx/shared/ui/PageHeader';
import { Card } from '@siliconx/shared/ui/Card';
import { PanelTitle } from '@siliconx/shared/ui/PanelTitle';
import { useAuth } from '../lib/authContext';
import { api, throwIfNotOk } from '../lib/api';
import type { AssetListResponse } from '../lib/types';
import { requireOrgFeature } from '../lib/orgFeatureGate';

/**
 * SIL-601 (Task 7 of plans/2026-08-10-org-wide-verification-links.md) —
 * customer-portal surface for the org-wide agentless verification link plus
 * the per-asset dedicated-link escape hatch (SIL-599's API).
 *
 * Placement: `team.tsx` and `integrations.tsx` are the closest analogs, but
 * neither fits exactly — every endpoint here except rotate is any-member
 * (per clientAccess.ts's `orgRole: 'member'` grants), matching
 * integrations.tsx's "visible to members, admin-gated actions inside" shape
 * rather than team.tsx's whole-page `adminOnly`. Nav entry lives in
 * Organization > Data & Integrations, next to Integrations, for the same
 * reason.
 */

// SIL-1108: `orgFeature: 'verificationLinks'` (customerNav.ts) is
// business-only — gated here so a direct hit doesn't fire this page's
// mount-time fetch before the root `OrgFeatureGate`'s (__root.tsx) redirect
// lands.
export const Route = createFileRoute('/verification-links')({
  beforeLoad: () => requireOrgFeature('verificationLinks'),
  component: VerificationLinksPage,
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OrgLink {
  shortCode: string;
  fullUrl: string;
}

interface AssetLink {
  assetId: string;
  shortCode: string;
  fullUrl: string;
  createdAt: string;
}

interface AssetOption {
  id: string;
  serialNumber: string | null;
  manufacturerName: string | null;
  modelName: string | null;
}

function assetLabel(a: AssetOption): string {
  const model = [a.manufacturerName, a.modelName].filter(Boolean).join(' ');
  return a.serialNumber ? `${model || 'Unknown model'} (${a.serialNumber})` : model || a.id;
}

// ---------------------------------------------------------------------------
// Toast
// ---------------------------------------------------------------------------

function Toast({ message, type, onDismiss }: { message: string; type: 'success' | 'error'; onDismiss: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, [onDismiss]);
  return (
    <div
      className={cn(
        'fixed bottom-6 right-6 z-50 rounded-lg px-4 py-3 text-sm font-medium shadow-lg',
        type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white',
      )}
    >
      {message}
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted"
    >
      {copied ? <Check className="size-3" /> : <Copy className="size-3" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Rotate confirmation dialog — its own component so the "old URL stops
// working immediately" copy is exactly one place, and so component tests
// can assert on it directly rather than mocking window.confirm.
// ---------------------------------------------------------------------------

function RotateConfirmDialog({
  open,
  busy,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-sm rounded-xl bg-card p-6 shadow-xl">
        <h2 className="mb-2 text-base font-semibold text-foreground">Rotate verification link?</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          The current URL will stop working immediately. Anyone who scanned or bookmarked the old
          link (KVM consoles, printed labels, saved bookmarks) will need the new one.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <Button onClick={onConfirm} disabled={busy}>
            {busy ? 'Rotating…' : 'Rotate link'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Org-link section
// ---------------------------------------------------------------------------

function OrgLinkSection({
  isAdmin,
  notify,
}: {
  isAdmin: boolean;
  notify: (message: string, type: 'success' | 'error') => void;
}) {
  const [link, setLink] = useState<OrgLink | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [rotating, setRotating] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.api.client['verification-links'].org
      .$get()
      .then(async (res) => {
        await throwIfNotOk(res);
        return (await res.json()) as unknown as OrgLink;
      })
      .then(setLink)
      .catch((ex: unknown) => setErr((ex as Error).message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  async function handleRotate() {
    setRotating(true);
    try {
      const res = await api.api.client['verification-links'].org.rotate.$post();
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      const rotated = (await res.json()) as unknown as OrgLink;
      setLink(rotated);
      setConfirmOpen(false);
      notify('Verification link rotated. The old URL no longer works.', 'success');
    } catch (ex: unknown) {
      notify((ex as Error).message, 'error');
    } finally {
      setRotating(false);
    }
  }

  return (
    <Card className="space-y-3">
      <div className="flex items-center gap-2">
        <Link2 className="size-5 shrink-0 text-muted-foreground" />
        <PanelTitle
          title="Org verification link"
          description="One short URL for the whole org. An operator navigates it from any asset's KVM/console to reach the verification harness — no per-asset link, no download."
          className="min-w-0 flex-1"
        />
      </div>

      {loading ? (
        <LoadingShell rows={1} />
      ) : err ? (
        <div className="text-xs text-red-600">Couldn&apos;t load the org link. {err}</div>
      ) : link ? (
        <div className="space-y-2">
          <div className="flex items-start gap-2 rounded-md border border-border bg-muted px-2 py-1.5">
            <code className="min-w-0 flex-1 overflow-x-auto whitespace-pre text-xs text-foreground">
              {link.fullUrl}
            </code>
            <CopyButton text={link.fullUrl} />
          </div>
          {isAdmin ? (
            <button
              type="button"
              onClick={() => setConfirmOpen(true)}
              className="inline-flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50"
            >
              <RefreshCw className="size-3" /> Rotate
            </button>
          ) : (
            <p className="text-xs text-muted-foreground">Only an org admin can rotate this link.</p>
          )}
        </div>
      ) : null}

      <RotateConfirmDialog
        open={confirmOpen}
        busy={rotating}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => void handleRotate()}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Asset picker — used by the bulk-generate action.
// ---------------------------------------------------------------------------

function AssetPickerDialog({
  open,
  onClose,
  onGenerate,
  busy,
}: {
  open: boolean;
  onClose: () => void;
  onGenerate: (assetIds: string[]) => void;
  busy: boolean;
}) {
  const [assets, setAssets] = useState<AssetOption[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!open) return;
    setAssets(null);
    setSelected(new Set());
    (async () => {
      try {
        const rows: AssetOption[] = [];
        let cursor: string | null = null;
        do {
          const res = await api.api.client.assets.$get({
            query: { limit: '200', ...(cursor ? { cursor } : {}) },
          });
          await throwIfNotOk(res);
          const page = (await res.json()) as unknown as AssetListResponse<AssetOption>;
          rows.push(...page.data);
          cursor = page.meta.nextCursor;
        } while (cursor);
        setAssets(rows);
      } catch (ex: unknown) {
        setErr((ex as Error).message);
      }
    })().catch(() => {});
  }, [open]);

  if (!open) return null;

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
      <div className="flex max-h-[80vh] w-full max-w-lg flex-col rounded-xl bg-card p-6 shadow-xl">
        <h2 className="mb-1 text-base font-semibold text-foreground">Generate asset links</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          Select the assets that need a dedicated verification link (curl-compatible, no fingerprint
          matching required).
        </p>

        {err ? (
          <div className="text-xs text-red-600">{err}</div>
        ) : assets === null ? (
          <LoadingShell rows={3} />
        ) : assets.length === 0 ? (
          <div className="text-sm text-muted-foreground">No assets found.</div>
        ) : (
          <div className="flex-1 space-y-1 overflow-y-auto rounded-md border border-border">
            {assets.map((a) => (
              <label
                key={a.id}
                className="flex cursor-pointer items-center gap-2 border-b border-border px-3 py-2 text-sm last:border-b-0 hover:bg-muted"
              >
                <input
                  type="checkbox"
                  checked={selected.has(a.id)}
                  onChange={() => toggle(a.id)}
                  className="size-3.5"
                />
                <span>{assetLabel(a)}</span>
              </label>
            ))}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <Button
            onClick={() => onGenerate(Array.from(selected))}
            disabled={busy || selected.size === 0}
          >
            {busy ? 'Generating…' : `Generate ${selected.size > 0 ? `(${selected.size})` : ''}`}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Revoke confirmation dialog
// ---------------------------------------------------------------------------

function RevokeConfirmDialog({
  open,
  busy,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40">
      <div className="w-full max-w-sm rounded-xl bg-card p-6 shadow-xl">
        <h2 className="mb-2 text-base font-semibold text-foreground">Revoke this asset&apos;s link?</h2>
        <p className="mb-4 text-sm text-muted-foreground">
          The short URL for this asset will stop working immediately. It is never reused — a new
          link (different code) can be generated later if needed.
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded-md px-4 py-2 text-sm font-medium text-muted-foreground hover:bg-muted disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {busy ? 'Revoking…' : 'Revoke link'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Asset-links section
// ---------------------------------------------------------------------------

function AssetLinksSection({ notify }: { notify: (message: string, type: 'success' | 'error') => void }) {
  const [links, setLinks] = useState<AssetLink[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState<string | null>(null);
  const [revoking, setRevoking] = useState(false);

  const load = useCallback(() => {
    api.api.client['verification-links'].assets
      .$get()
      .then(async (res) => {
        await throwIfNotOk(res);
        return (await res.json()) as unknown as AssetLink[];
      })
      .then(setLinks)
      .catch((ex: unknown) => setErr((ex as Error).message));
  }, []);

  useEffect(load, [load]);

  async function handleGenerate(assetIds: string[]) {
    if (assetIds.length === 0) return;
    setGenerating(true);
    try {
      const res = await api.api.client['verification-links'].assets.$post({ json: { assetIds } });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      setPickerOpen(false);
      load();
      notify(`Generated ${assetIds.length} link${assetIds.length !== 1 ? 's' : ''}.`, 'success');
    } catch (ex: unknown) {
      notify((ex as Error).message, 'error');
    } finally {
      setGenerating(false);
    }
  }

  async function handleRevoke() {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      const res = await api.api.client['verification-links'].assets[':assetId'].$delete({
        param: { assetId: revokeTarget },
      });
      if (!res.ok) {
        await throwIfNotOk(res);
        throw new Error(`HTTP ${res.status}`);
      }
      setLinks((prev) => prev?.filter((l) => l.assetId !== revokeTarget) ?? null);
      setRevokeTarget(null);
      notify('Link revoked.', 'success');
    } catch (ex: unknown) {
      notify((ex as Error).message, 'error');
    } finally {
      setRevoking(false);
    }
  }

  return (
    <Card className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <PanelTitle
          title="Asset-specific links"
          description="Dedicated links for assets too homogeneous for fingerprint auto-match to trust. Curl-compatible — no browser required."
        />
        <Button size="sm" onClick={() => setPickerOpen(true)}>
          Bulk-generate
        </Button>
      </div>

      {err ? (
        <div className="text-xs text-red-600">{err}</div>
      ) : links === null ? (
        <LoadingShell rows={2} />
      ) : links.length === 0 ? (
        <p className="text-sm text-muted-foreground">No dedicated asset links yet.</p>
      ) : (
        <div className="rounded-lg border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted text-left text-xs font-medium text-muted-foreground">
                <th className="px-4 py-2">Asset</th>
                <th className="px-4 py-2">Link</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {links.map((l) => (
                <tr key={l.assetId}>
                  <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{l.assetId}</td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <code className="text-xs">{l.fullUrl}</code>
                      <CopyButton text={l.fullUrl} />
                    </div>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => setRevokeTarget(l.assetId)}
                      className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="size-3" /> Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AssetPickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onGenerate={(ids) => void handleGenerate(ids)}
        busy={generating}
      />

      <RevokeConfirmDialog
        open={revokeTarget !== null}
        busy={revoking}
        onCancel={() => setRevokeTarget(null)}
        onConfirm={() => void handleRevoke()}
      />
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function VerificationLinksPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const notify = useCallback((message: string, type: 'success' | 'error') => setToast({ message, type }), []);

  return (
    <div className="space-y-6">
      <div>
        <PageHeader title="Verification Links" pinTo="/verification-links" />
        <p className="mt-1 text-sm text-muted-foreground">
          Manage the org-wide agentless verification link and per-asset dedicated links used to
          reach the SiliconX verification harness.
        </p>
      </div>

      <OrgLinkSection isAdmin={isAdmin} notify={notify} />
      <AssetLinksSection notify={notify} />

      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
    </div>
  );
}
